// src/Services/accountService.js
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";
import { firstNameOf } from "../utils/validateDisplayName";

class AccountService {
  // Email management
  async saveEmail(email) {
    try {
      const userId = await authService.getCurrentUserId();
      const userRef = doc(db, "users", userId);
      
      await updateDoc(userRef, {
        savedEmail: email,
        emailSavedAt: new Date(),
        'accountSettings.emailForFuturePurchases': true
      });
      
      console.log("✅ Email saved successfully:", email);
      return { success: true };
    } catch (error) {
      console.error("❌ Error saving email:", error);
      throw error;
    }
  }

  async getSavedEmail() {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return null; // a visitor who hasn't done anything has no account yet
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        return data.savedEmail || null;
      }
      
      return null;
    } catch (error) {
      console.error("❌ Error getting saved email:", error);
      return null;
    }
  }

  async removeEmail() {
    try {
      const userId = await authService.getCurrentUserId();
      const userRef = doc(db, "users", userId);
      
      await updateDoc(userRef, {
        savedEmail: null,
        emailSavedAt: null,
        'accountSettings.emailForFuturePurchases': false
      });
      
      console.log("✅ Email removed successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ Error removing email:", error);
      throw error;
    }
  }

  // Transaction history
  async getBillingHistory() {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return []; // a visitor who hasn't done anything has no account yet
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const history = data.coinPurchaseHistory || [];
        
        // Sort by timestamp (newest first)
        return history.sort((a, b) => {
          const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
          const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
          return dateB - dateA;
        });
      }
      
      return [];
    } catch (error) {
      console.error("❌ Error getting billing history:", error);
      return [];
    }
  }

  async getSpendingHistory() {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return []; // a visitor who hasn't done anything has no account yet
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const history = data.coinSpendingHistory || [];
        
        // Sort by timestamp (newest first)
        return history.sort((a, b) => {
          const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
          const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
          return dateB - dateA;
        });
      }
      
      return [];
    } catch (error) {
      console.error("❌ Error getting spending history:", error);
      return [];
    }
  }

  // Account summary
  async getAccountSummary() {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return null; // a visitor who hasn't done anything has no account yet
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        return {
          userId,
          savedEmail: data.savedEmail || null,
          coinBalance: data.coinBalance || 0,
          totalCoinsSpent: data.totalCoinsSpent || 0,
          createdAt: data.createdAt,
          purchaseCount: (data.coinPurchaseHistory || []).length,
          roomCount: (data.recentRooms || []).length, // Use recentRooms for room count
          lastPurchase: this.getLastTransaction(data.coinPurchaseHistory),
          lastSpending: this.getLastTransaction(data.coinSpendingHistory)
        };
      }
      
      return null;
    } catch (error) {
      console.error("❌ Error getting account summary:", error);
      return null;
    }
  }

  // Helper method to get last transaction
  getLastTransaction(history) {
    if (!history || history.length === 0) return null;
    
    const sorted = history.sort((a, b) => {
      const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return dateB - dateA;
    });
    
    return sorted[0];
  }

  // Check if user has saved email (for purchase flow)
  async hasSavedEmail() {
    const email = await this.getSavedEmail();
    return !!email;
  }

  // The player's display name: users/{uid}.displayName first (what mobile and
  // the leaderboard read and write), then the Auth profile. Never creates an
  // account; a visitor who hasn't done anything gets null.
  // Pass { savedOnly: true } to skip that fallback and see whether the
  // account has a game name of its own.
  async getUserDisplayName({ savedOnly = false } = {}) {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return null;

      const userDoc = await getDoc(doc(db, "users", userId));
      const stored = userDoc.exists() ? userDoc.data().displayName : null;
      if (stored || savedOnly) return stored || null;

      // No game name yet: start from the account's first name.
      return firstNameOf(authService.auth.currentUser?.displayName) || null;
    } catch (error) {
      console.error("❌ Error getting user display name:", error);
      return null;
    }
  }
}

// Export singleton instance
export const accountService = new AccountService();
export default accountService;